import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';
import { ApiResponse } from '../lib/api';
import './Settings.css';

interface Organization {
  id: number;
  name: string;
  org_code: string;
}

interface UserData {
  id: number;
  name: string | null;
  phone: string | null;
  role: number;
  org_id: number | null;
  org_name: string;
  org_code: string | null;
  user_id: string;
  org?: Organization;
}

interface DatabaseUserData {
  id: number;
  name: string | null;
  phone: string | null;
  role: number;
  org_id: number | null;
  user_id: string;
  org: Organization | null;
}

const getRoleName = (role: number): string => {
  switch (role) {
    case 1:
      return 'Usuario';
    case 2:
      return 'Administrador';
    case 3:
      return 'Super Administrador';
    case 4:
      return 'Super Usuario';
    default:
      return 'Desconocido';
  }
};

const AdminSettings = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPreflighting, setIsPreflighting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  useEffect(() => {
    fetchUserData();
    fetchOrganizations();
  }, []);

  const fetchUserData = async () => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No session found');
      }

      const { data: dbUserData, error } = await supabase
        .from('user_data')
        .select(`
          id,
          name,
          phone,
          role,
          org_id,
          user_id,
          org:org (
            id,
            name,
            org_code
          )
        `)
        .eq('user_id', session.data.session.user.id)
        .single() as { data: DatabaseUserData | null; error: any };

      if (error) throw error;

      if (dbUserData) {
        const userData: UserData = {
          ...dbUserData,
          org_name: dbUserData.org?.name ?? '',
          org_code: dbUserData.org?.org_code ?? null,
          org: dbUserData.org ?? undefined
        };
        setUserData(userData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setMessage('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('org')
        .select('id, name, org_code')
        .order('name');

      if (error) throw error;

      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData) return;

    setIsPreflighting(true);
    setIsSaving(false);
    setMessage('');

    try {
      // Check session validity before request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.location.href = '/';
        return;
      }

      // Log raw user data for debugging
      console.log('Raw userData:', {
        name: userData.name,
        phone: userData.phone,
        role: userData.role,
        org_id: userData.org_id,
        types: {
          name: typeof userData.name,
          phone: typeof userData.phone,
          role: typeof userData.role,
          org_id: typeof userData.org_id
        }
      });

      // Prepare update data with type validation and undefined handling
      const updateData = {
        ...(userData.name !== null && typeof userData.name === 'string' && userData.name.trim() !== '' && { 
          name: userData.name.trim() 
        }),
        ...(userData.phone !== null && typeof userData.phone === 'string' && userData.phone.trim() !== '' && { 
          phone: userData.phone.trim() 
        }),
        ...(userData.role !== null && typeof userData.role === 'number' && userData.role >= 1 && userData.role <= 4 && { 
          role: userData.role 
        }),
        ...(userData.org_id !== null && typeof userData.org_id === 'number' && userData.org_id > 0 && { 
          org_id: userData.org_id 
        })
      };

      // Check if there's data to update
      if (Object.keys(updateData).length === 0) {
        setMessage('No hay datos para actualizar');
        return;
      }

      // Validate data types and constraints
      if (updateData.name && (updateData.name.length < 2 || updateData.name.length > 100)) {
        setMessage('El nombre debe tener entre 2 y 100 caracteres');
        return;
      }

      if (updateData.phone && !/^\+?[\d\s-]{8,20}$/.test(updateData.phone)) {
        setMessage('El teléfono debe tener un formato válido');
        return;
      }

      if (updateData.role && (updateData.role < 1 || updateData.role > 4)) {
        setMessage('Rol inválido');
        return;
      }

      // Log request details in development
      console.log('Sending update request:', {
        url: '/api/users/update_data',
        data: updateData,
        rawData: JSON.stringify(updateData),
        dataLength: Object.keys(updateData).length,
        session: {
          user: session.user.email,
          expires_at: session.expires_at
        }
      });

      setIsPreflighting(false);
      setIsSaving(true);

      const response = await api.put<ApiResponse>('/api/users/update_data', updateData);

      // Log the complete response for debugging
      console.log('Complete response:', response);

      // Handle different status codes explicitly
      switch (response.statusCode) {
        case 401:
          window.location.href = '/';
          return;
        case 403:
          throw new Error('No tienes permisos para realizar esta acción');
        case 400:
          console.error('Bad Request Details:', response);
          throw new Error(response.detail || response.error || 'Datos inválidos');
        case 500:
          throw new Error('Error interno del servidor');
      }

      if (!response.ok) {
        console.error('Error response:', response);
        throw new Error(response.error || 'Error al actualizar los datos');
      }

      setMessage('Cambios guardados exitosamente');
      // Refresh data after successful update
      await fetchUserData();
    } catch (error) {
      console.error('Error updating user data:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      setMessage(error instanceof Error ? error.message : 'Error al guardar los cambios');
    } finally {
      setIsPreflighting(false);
      setIsSaving(false);
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setMessage('');
    setIsSaving(true);

    try {
      const orgCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const response = await api.post<ApiResponse>('/api/organizations', {
        name: newOrgName.trim(),
        org_code: orgCode
      });

      if (!response.ok) {
        throw new Error(response.error || response.detail || 'Error al crear la organización');
      }

      setMessage('Organización creada exitosamente');
      setShowCreateOrgModal(false);
      setNewOrgName('');
      await fetchOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      setMessage(error instanceof Error ? error.message : 'Error al crear la organización');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (!userData) {
    return <div>Error al cargar los datos</div>;
  }

  let buttonText;
  if (isPreflighting) {
    buttonText = 'Verificando...';
  } else if (isSaving) {
    buttonText = 'Guardando...';
  } else {
    buttonText = 'Guardar Cambios';
  }

  return (
    <div className="settings-container">
      <h2>Configuración de Administrador</h2>
      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <label>Nombre:</label>
          <input
            type="text"
            value={userData.name || ''}
            onChange={(e) => setUserData({ ...userData, name: e.target.value })}
            disabled={isPreflighting || isSaving}
          />
        </div>

        <div className="form-group">
          <label>Teléfono:</label>
          <input
            type="tel"
            value={userData.phone || ''}
            onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
            disabled={isPreflighting || isSaving}
          />
        </div>

        <div className="form-group">
          <label>Rol:</label>
          <input
            type="text"
            value={getRoleName(userData.role)}
            readOnly
            disabled
          />
        </div>

        <div className="form-group">
          <label>Organización:</label>
          {userData.role === 4 ? (
            <>
              <select
                value={userData.org_id || ''}
                onChange={(e) => {
                  const selectedOrg = organizations.find(org => org.id === Number(e.target.value));
                  setUserData({
                    ...userData,
                    org_id: Number(e.target.value),
                    org_name: selectedOrg?.name || '',
                    org_code: selectedOrg?.org_code || null
                  });
                }}
                className="org-select"
                disabled={isPreflighting || isSaving}
              >
                <option value="">Seleccionar organización</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <div className="org-code">
                {userData.org_code && (
                  <span>Código de organización: <strong>{userData.org_code}</strong></span>
                )}
              </div>
              <button
                type="button"
                className="create-org-button"
                onClick={() => setShowCreateOrgModal(true)}
                disabled={isPreflighting || isSaving}
              >
                Crear Nueva Organización
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={userData.org_name}
                readOnly
                disabled
              />
              <div className="org-code">
                {userData.org_code && (
                  <span>Código de organización: <strong>{userData.org_code}</strong></span>
                )}
              </div>
            </>
          )}
        </div>

        <button 
          type="submit" 
          disabled={isPreflighting || isSaving}
          className="primary-button w-full"
        >
          {buttonText}
        </button>

        {message && (
          <div className={`message ${message.includes('Error') || message.includes('error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </form>

      {showCreateOrgModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Crear Nueva Organización</h3>
            <form onSubmit={handleCreateOrg}>
              <div className="form-group">
                <label htmlFor="orgName">Nombre de la Organización:</label>
                <input
                  id="orgName"
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  required
                  disabled={isSaving}
                />
              </div>
              <div className="modal-buttons">
                <button type="submit" disabled={isSaving}>
                  {isSaving ? 'Creando...' : 'Crear'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowCreateOrgModal(false)}
                  disabled={isSaving}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings; 